import Skeleton from "./Skeleton";
import SkeletonTextLine from "./SkeletonTextLine";
import SkeletonPublicationCard from "./SkeletonPublicationCard";

/**
 * Loading silhouette for {@link PlaylistPageHeader}: the blurred hero band
 * (48px + safe-area-top), then the info block — header title (12/18), title
 * (24/~34), two description lines (14/~20), an org logo (30px) + name row — and
 * the article-count row. Shown while the playlist itself is still loading (no
 * cache seed). Once the playlist is seeded/loaded the real header renders and
 * only the rows below fall back to {@link PlaylistRowsSkeleton}.
 */
export function PlaylistPageHeaderSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      {/* Blurred hero band — same height as the real header. */}
      <Skeleton
        $variant="rectangular"
        width="100%"
        height="calc(48px + var(--safe-area-top))"
      />

      <div className="flex flex-col gap-3 px-5 py-2">
        <div className="flex flex-col gap-2 pt-1">
          {/* headerTitle (12/18). */}
          <SkeletonTextLine width={90} fontSize={12} lineHeight={18} />
          {/* Title (24, leading-1.4 ≈ 34). */}
          <SkeletonTextLine width="80%" fontSize={24} lineHeight={34} />
          {/* Description (14, ~20). */}
          <div className="flex flex-col">
            <SkeletonTextLine width="100%" fontSize={14} lineHeight={20} />
            <SkeletonTextLine width="60%" fontSize={14} lineHeight={20} />
          </div>
          {/* Org logo (30px) + name. */}
          <div className="flex items-center gap-2">
            <Skeleton $variant="circular" width={30} height={30} />
            <SkeletonTextLine width={110} fontSize={10} lineHeight={14} />
          </div>
        </div>

        {/* Article-count row (icon + label). */}
        <div className="flex items-center gap-1">
          <Skeleton $variant="rectangular" width={16} height={16} />
          <SkeletonTextLine width={64} fontSize={12} lineHeight={16} />
        </div>
      </div>
    </div>
  );
}

/**
 * Loading silhouette for the playlist's article/video rows — mirrors
 * PlaylistPage's `divide-y ... px-4 pt-4 *:py-4` list. Rows are image-carrying
 * publication cards without a description, matching ArticlePublicationCard
 * (`showImage`, `description={null}`). `count` comes from the seeded playlist's
 * content ids so the list reserves the right height before the batch resolves.
 */
export function PlaylistRowsSkeleton({
  count,
}: {
  count: number;
}): React.JSX.Element {
  return (
    <div
      className="flex w-full flex-col divide-y divide-ds-slate-100 px-4 pt-4 *:py-4"
      style={{ paddingBottom: "calc(16px + var(--safe-area-bottom))" }}
    >
      {Array.from({ length: count }).map((_, idx) => (
        <SkeletonPublicationCard showImage key={idx} showDescription={false} />
      ))}
    </div>
  );
}

/**
 * Full-page boot placeholder for the playlist page, shown by the AppRoutes boot
 * gate when a playlist URL is opened cold (before the route mounts). Composes
 * the same header + rows silhouettes the page itself falls back to, so the
 * hand-off to the real page is seamless.
 */
export default function PlaylistBootSkeleton(): React.JSX.Element {
  return (
    <div className="flex size-full flex-col overflow-hidden bg-white">
      <div className="mx-auto w-full md:max-w-[760px] md:px-10">
        <PlaylistPageHeaderSkeleton />
        <PlaylistRowsSkeleton count={6} />
      </div>
    </div>
  );
}
